import * as React from 'react'
import { Plug } from 'lucide-react'
import type { MyConnectionDto, OrgConnectionDto } from '@lib/dto/mcp.dto'
import { MCP_ACCESS_LEVEL_LABELS } from '@lib/enums/mcp-access-level.enum'
import type { McpOrgRemovalReason } from '@lib/enums/mcp-org-removal-reason.enum'
import { AppCell, DeviceCell, EndAccessDialog, NoConnectedApps, whenLabel } from '@/components/mcp/ConnectionParts'
import { PageHeader } from '@/components/PageHeader'
import { EmptyState, ErrorState, LoadingState } from '@/components/PageState'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { toast } from '@/components/ui/toast'
import { canManageTeam, useDashboardScope } from '@/dashboard/DashboardScope'
import { ReadOnlyBanner } from '@/dashboard/ReadOnlyBanner'
import { apiErrorMessage } from '@/helpers/apiErrorMessage'
import { useConnectedAppsActions, useMcpEnabled, useMcpStatus, useMyConnections, useOrgConnections } from '@/hooks/useConnectedApps'
import { cn } from '@/lib/utils'

/** Why an organization was taken off a connection, in the person's words. */
const REMOVED_LABELS: Record<McpOrgRemovalReason, string> = {
  owner_removed: 'removed by an Owner',
  setting_off: 'MCP turned off',
  member_removed: 'no longer a member',
  tenant_closed: 'organization closed',
}

function removedLabel(reason: McpOrgRemovalReason | null): string {
  return reason ? REMOVED_LABELS[reason] : 'removed'
}

function MyConnections() {
  const query = useMyConnections({ enabled: true })
  const actions = useConnectedAppsActions()
  const [ending, setEnding] = React.useState<MyConnectionDto | null>(null)

  if (query.isLoading) return <LoadingState />
  if (query.isError || !query.data) return <ErrorState message={apiErrorMessage(query.error, 'Could not load your connected apps.')} />
  if (query.data.connections.length === 0) return <NoConnectedApps />
  return (
    <>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>App</TableHead>
            <TableHead>Signed in from</TableHead>
            <TableHead>Connected</TableHead>
            <TableHead>Last used</TableHead>
            <TableHead>Access</TableHead>
            <TableHead className="text-right">
              <span className="sr-only">Actions</span>
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {query.data.connections.map((connection) => (
            <TableRow key={connection.id}>
              <TableCell>
                <AppCell app={connection.app} />
              </TableCell>
              <TableCell>
                <DeviceCell device={connection.device} signInIp={connection.signInIp} />
              </TableCell>
              <TableCell className="whitespace-nowrap text-muted-foreground">{new Date(connection.createdAt).toLocaleDateString()}</TableCell>
              <TableCell className="whitespace-nowrap text-muted-foreground">{whenLabel(connection.lastUsedAt)}</TableCell>
              <TableCell>
                <div className="flex flex-wrap gap-1">
                  {connection.organizations.map((org) => (
                    <Badge key={org.tenantId} className={cn(org.removed ? 'text-muted-foreground' : null)}>
                      {org.name}: {org.removed ? removedLabel(org.removedReason) : MCP_ACCESS_LEVEL_LABELS[org.level]}
                    </Badge>
                  ))}
                </div>
              </TableCell>
              <TableCell className="text-right">
                <Button variant="destructive-quiet" size="sm" onClick={() => setEnding(connection)}>
                  Disconnect
                </Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <EndAccessDialog
        open={ending !== null}
        title={`Disconnect ${ending?.app.name ?? 'this app'}?`}
        description={`${ending?.app.name ?? 'The app'} loses access to PixelSign right away. You can connect it again later.`}
        confirmLabel="Disconnect"
        onClose={() => setEnding(null)}
        onConfirm={async () => {
          if (!ending) return
          await actions.disconnect.mutateAsync(ending.id)
          toast.success(`Disconnected ${ending.app.name}`)
        }}
      />
    </>
  )
}

function OrgConnections({ canRemove }: { canRemove: boolean }) {
  const scope = useDashboardScope()
  const query = useOrgConnections({ enabled: true })
  const actions = useConnectedAppsActions()
  const [removing, setRemoving] = React.useState<OrgConnectionDto | null>(null)

  if (query.isLoading) return <LoadingState />
  if (query.isError || !query.data) return <ErrorState message={apiErrorMessage(query.error, 'Could not load this organization’s connected apps.')} />
  if (query.data.connections.length === 0) {
    return <EmptyState icon={Plug} title="No connected apps" description={`Nobody has connected an AI app to ${scope.tenantName}.`} />
  }
  return (
    <>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Person</TableHead>
            <TableHead>App</TableHead>
            <TableHead>Access here</TableHead>
            <TableHead>Last used here</TableHead>
            <TableHead className="text-right">
              <span className="sr-only">Actions</span>
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {query.data.connections.map((connection) => (
            <TableRow key={connection.id}>
              <TableCell>
                <div className="font-medium">{connection.person.name}</div>
                <div className="text-caption text-muted-foreground">{connection.person.email}</div>
              </TableCell>
              <TableCell>
                <AppCell app={connection.app} />
              </TableCell>
              <TableCell>
                <Badge tone={connection.level === 'live' ? 'warning' : 'neutral'}>{MCP_ACCESS_LEVEL_LABELS[connection.level]}</Badge>
              </TableCell>
              <TableCell className="whitespace-nowrap text-muted-foreground">{whenLabel(connection.lastUsedAt)}</TableCell>
              <TableCell className="text-right">
                {canRemove ? (
                  <Button variant="destructive-quiet" size="sm" onClick={() => setRemoving(connection)}>
                    Remove from {scope.tenantName}
                  </Button>
                ) : null}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <EndAccessDialog
        open={removing !== null}
        title={`Remove ${removing?.app.name ?? 'this app'} from ${scope.tenantName}?`}
        description={`It can’t reach ${scope.tenantName} any more. ${removing?.person.name ?? 'They'} keeps using ${removing?.app.name ?? 'it'} with their other organizations.`}
        confirmLabel="Remove"
        onClose={() => setRemoving(null)}
        onConfirm={async () => {
          if (!removing) return
          await actions.removeFromOrg.mutateAsync(removing.id)
          toast.success(`Removed ${removing.app.name} from ${scope.tenantName}`)
        }}
      />
    </>
  )
}

/**
 * AI apps connected over MCP: the person's own (not shown when staff view
 * as the organization), and for Owners and staff, everyone's connections
 * that reach this organization. Disconnecting and removing are immediate.
 */
export function ConnectedAppsPage() {
  const scope = useDashboardScope()
  const status = useMcpStatus()
  const enabled = useMcpEnabled()
  const viewAs = scope.cacheKey === 'staff'
  const seesOrg = scope.role === 'owner' || viewAs

  function renderBody() {
    if (status.isLoading) return <LoadingState />
    if (!enabled) {
      return <EmptyState icon={Plug} title="Not available yet" description="Connecting AI apps to PixelSign isn’t switched on yet." />
    }
    return (
      <div className="space-y-8">
        {viewAs ? null : (
          <section className="space-y-3">
            <h2 className="text-title font-semibold">Your connections</h2>
            <MyConnections />
          </section>
        )}
        {seesOrg ? (
          <section className="space-y-3">
            <h2 className="text-title font-semibold">Connections to {scope.tenantName}</h2>
            <OrgConnections canRemove={canManageTeam(scope)} />
          </section>
        ) : null}
      </div>
    )
  }

  return (
    <>
      <PageHeader title="Connected apps" description="AI apps connected to PixelSign over MCP, and what they can reach." />
      <ReadOnlyBanner />
      {renderBody()}
    </>
  )
}
